import { Injectable, OnDestroy } from '@angular/core';
import { hasClientSmallScreen } from 'environments/environment-helpers';
import { PubSubService, navigationStackChangedEvent, PubSubRegistration } from 'services/pubsub.service';
import { SettingsService, ScrollHideMode } from 'services/settings.service';

export interface ScrollHideRegistration {
  unregister: () => void;
}

export interface ScrollHideOptions {
  element: HTMLElement;
  direction: 'up' | 'down'; // up = hide by translating up, down = hide by translating down
  onScrollChange: (translateY: number) => void;
  enabled?: () => boolean;
  hideType?: 'default' | 'auto'; // auto = only hides with Auto setting, default = hides with Always or Auto
}

interface RegisteredComponent {
  options: ScrollHideOptions;
  height: number;
  isHidden: boolean;
}


@Injectable({
  providedIn: 'root'
})
export class ScrollHideService implements OnDestroy {
  private registeredComponents = new Map<HTMLElement, RegisteredComponent>();
  private scrollListener: (() => void) | null = null;
  private tabChangeObserver: MutationObserver | null = null;
  private pageActivationObserver: MutationObserver | null = null;
  private scrollAreaElement: HTMLElement | null = null;
  private globalLastScrollTop = 0;
  private navigationSubscription: PubSubRegistration | null = null;
  private isAtBottom = false;

  // service-level scroll tracking for unified hide/show
  private scrollAccumulator = 0;
  private lastScrollDirection: 'up' | 'down' | null = null;
  private isSuspended = false;

  constructor(
    private pubSub: PubSubService,
    private settings: SettingsService
  ) {
    // subscribe to navigation events to automatically refresh scroll detection
    // when navigating between pages (handles both forward and back navigation)
    this.navigationSubscription = this.pubSub.subscribe(navigationStackChangedEvent, () => {
      // use requestAnimationFrame to wait for DOM updates after navigation
      requestAnimationFrame(() => {
        this.refresh();
      });
    });
  }

  ngOnDestroy() {
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
      this.navigationSubscription = null;
    }
    this.cleanup();
  }

  /**
   * Register a component for scroll-based hiding
   */
  register(options: ScrollHideOptions): ScrollHideRegistration {
    const element = options.element;

    // if this is the first registration, set up scroll detection
    if (this.registeredComponents.size === 0) {
      this.setupScrollDetection();
    }

    // store component info
    const componentInfo: RegisteredComponent = {
      options,
      height: element.offsetHeight || 0,
      isHidden: false
    };

    this.registeredComponents.set(element, componentInfo);

    // return unregister function
    return {
      unregister: () => {
        this.unregister(element);
      }
    };
  }

  /**
   * Unregister a component
   */
  private unregister(element: HTMLElement) {
    this.registeredComponents.delete(element);

    // if no more components, clean up
    if (this.registeredComponents.size === 0) {
      this.cleanup();
    }
  }

  /**
   * Set up scroll detection on the active cs-content
   */
  private setupScrollDetection() {
    // clean up existing listener first
    if (this.scrollListener) {
      this.scrollListener();
      this.scrollListener = null;
    }

    // find the active page's cs-content scroll area
    const activePage = document.querySelector('.cs-page.active');
    if (!activePage) {
      // page not active yet (can happen on initial app load), set up observer to wait for it
      this.setupPageActivationObserver();
      return;
    }

    // clean up activation observer if it exists (page is now active)
    if (this.pageActivationObserver) {
      this.pageActivationObserver.disconnect();
      this.pageActivationObserver = null;
    }

    // try to find active cs-content first (for multi-tab pages like home)
    // if not found, fall back to any cs-content (for single-content pages)
    let contentElement = activePage.querySelector('cs-content.active');
    if (!contentElement) {
      contentElement = activePage.querySelector('cs-content');
    }

    if (!contentElement) {
      return;
    }

    // find the scrollable area inside cs-content
    const scrollableArea = contentElement.querySelector('.scroll-area');
    if (!scrollableArea) {
      return;
    }
    this.scrollAreaElement = scrollableArea as HTMLElement;

    // add scroll event listener
    const handleScroll = this.handleScroll.bind(this);
    this.scrollAreaElement.addEventListener('scroll', handleScroll, { passive: true });
    this.scrollListener = () => {
      if (this.scrollAreaElement) {
        this.scrollAreaElement.removeEventListener('scroll', handleScroll);
      }
    };

    // reset scroll position and state
    this.globalLastScrollTop = this.scrollAreaElement.scrollTop || 0;
    this.scrollAccumulator = 0;
    this.lastScrollDirection = null;

    // reset component tracking state and visual state when returning to page
    this.registeredComponents.forEach(component => {
      component.isHidden = false;
      component.options.onScrollChange(0);
    });

    // set up or re-initialize the MutationObserver
    this.setupTabChangeObserver();
  }

  /**
   * Set up MutationObserver to watch for tab changes
   */
  private setupTabChangeObserver() {
    // clean up existing observer first
    if (this.tabChangeObserver) {
      this.tabChangeObserver.disconnect();
      this.tabChangeObserver = null;
    }

    // find the active page to watch for cs-content class changes
    const activePage = document.querySelector('.cs-page.active');
    if (!activePage) {
      return;
    }

    this.tabChangeObserver = new MutationObserver((mutations) => {
      // check if any mutation affected the 'active' class on cs-content elements
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' &&
            mutation.attributeName === 'class' &&
            mutation.target instanceof HTMLElement &&
            mutation.target.tagName.toLowerCase() === 'cs-content') {
          // wait for Angular to complete the update before re-setting up scroll detection
          requestAnimationFrame(() => {
            this.setupScrollDetection();
          });
          break;
        }
      }
    });

    // observe class changes on cs-content elements within the active page
    this.tabChangeObserver.observe(activePage, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true
    });
  }

  /**
   * Set up MutationObserver to wait for page activation on initial app load
   */
  private setupPageActivationObserver() {
    // if already observing, don't create another
    if (this.pageActivationObserver) {
      return;
    }

    // find the nav container where cs-page elements live
    const navContainer = document.querySelector('cs-nav');
    if (!navContainer) {
      return;
    }

    this.pageActivationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' &&
            mutation.attributeName === 'class' &&
            mutation.target instanceof HTMLElement &&
            mutation.target.classList.contains('cs-page') &&
            mutation.target.classList.contains('active')) {
          // page is now active, set up scroll detection
          requestAnimationFrame(() => {
            this.setupScrollDetection();
          });
          return;
        }
      }
    });

    this.pageActivationObserver.observe(navContainer, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true
    });
  }

  /**
   * Handle scroll events with hysteresis-based hiding
   * All components hide/show together using header height as threshold
   */
  private handleScroll(event: Event) {
    // skip scroll handling when suspended (e.g., during programmatic scrolls)
    if (this.isSuspended) {
      return;
    }

    // only apply scroll animation on small screens
    if (!hasClientSmallScreen()) {
      // reset all components on large screens
      this.registeredComponents.forEach(component => {
        if (component.isHidden) {
          component.isHidden = false;
          component.options.onScrollChange(0);
        }
      });
      return;
    }

    const scrollElement = event.target as HTMLElement;
    const scrollTop = scrollElement.scrollTop;
    const scrollHeight = scrollElement.scrollHeight;
    const clientHeight = scrollElement.clientHeight;

    // detect iOS bounce - ignore scroll events during bounce
    const maxScroll = scrollHeight - clientHeight;
    const isBouncingTop = scrollTop < 0;
    const isBouncingBottom = scrollTop > maxScroll;

    // during iOS bounce, don't update state
    if (isBouncingTop || isBouncingBottom) {
      this.globalLastScrollTop = scrollTop;
      return;
    }

    const scrollDelta = scrollTop - this.globalLastScrollTop;

    // skip if no scroll happened
    if (scrollDelta === 0) {
      return;
    }

    const currentDirection: 'up' | 'down' = scrollDelta > 0 ? 'down' : 'up';

    // check if user has scrolled to the bottom (with small threshold for precision)
    const atBottom = (scrollTop + clientHeight) >= (scrollHeight - 5);

    // if direction changed, reset accumulator
    if (this.lastScrollDirection !== currentDirection) {
      this.scrollAccumulator = 0;
      this.lastScrollDirection = currentDirection;
    }

    // accumulate scroll distance
    this.scrollAccumulator += Math.abs(scrollDelta);

    // update component heights
    this.registeredComponents.forEach(component => {
      component.height = component.options.element.offsetHeight || 0;
    });

    // fixed threshold for hide/show detection
    const threshold = 50;

    // calculate max component height (used to determine if hiding makes sense)
    let maxComponentHeight = 0;
    this.registeredComponents.forEach(component => {
      if (component.height > maxComponentHeight) {
        maxComponentHeight = component.height;
      }
    });

    // check if there's enough remaining scroll distance to justify hiding
    // if we're close to bottom and hiding would just immediately trigger unhide, skip hiding
    const remainingScroll = maxScroll - scrollTop;
    const shouldSkipHiding = currentDirection === 'down' && remainingScroll < maxComponentHeight;

    // when threshold reached, hide/show ALL components together
    if (this.scrollAccumulator >= threshold) {
      // if near bottom while scrolling down, don't change hide state
      // (components will be shown when hitting actual bottom via atBottom check)
      if (!shouldSkipHiding) {
        const shouldHide = currentDirection === 'down';

        this.registeredComponents.forEach(component => {
          // skip components that are not on the active page
          const pageElement = component.options.element.closest('.cs-page');
          if (pageElement && !pageElement.classList.contains('active')) {
            return;
          }

          // skip disabled components
          if (component.options.enabled && !component.options.enabled()) {
            if (component.isHidden) {
              component.isHidden = false;
              component.options.onScrollChange(0);
            }
            return;
          }

          // check if component should hide based on settings
          if (!this.shouldComponentHide(component)) {
            // ensure component is visible if it shouldn't hide
            if (component.isHidden) {
              component.isHidden = false;
              component.options.onScrollChange(0);
            }
            return;
          }

          if (component.isHidden !== shouldHide) {
            component.isHidden = shouldHide;
            const translateY = shouldHide
              ? (component.options.direction === 'up' ? -component.height : component.height)
              : 0;
            component.options.onScrollChange(translateY);
          }
        });
      }

      this.scrollAccumulator = 0;
    }

    this.globalLastScrollTop = scrollTop;

    // if at bottom and elements are hidden, show them
    if (atBottom && !this.isAtBottom) {
      this.isAtBottom = true;
      this.showAllComponents();
    } else if (!atBottom && this.isAtBottom) {
      this.isAtBottom = false;
    }
  }

  /**
   * Check if a component should hide based on settings and hideType
   */
  private shouldComponentHide(component: RegisteredComponent): boolean {
    const hideType = component.options.hideType || 'default';
    const direction = component.options.direction;

    // get the relevant setting based on direction
    const setting = direction === 'up'
      ? this.settings.headerScrollHide
      : this.settings.footerScrollHide;

    // determine if hiding should occur
    switch (setting) {
      case ScrollHideMode.Never:
        return false;
      case ScrollHideMode.Always:
        return true;
      case ScrollHideMode.Auto:
        return hideType === 'auto';
      default:
        return true;
    }
  }

  /**
   * Show all components (used when scrolled to bottom)
   * CSS transitions handle the animation
   */
  private showAllComponents() {
    this.scrollAccumulator = 0;
    this.registeredComponents.forEach(component => {
      // check if component is enabled
      if (component.options.enabled && !component.options.enabled()) {
        return;
      }

      if (component.isHidden) {
        component.isHidden = false;
        component.options.onScrollChange(0);
      }
    });
  }

  /**
   * Clean up all listeners and observers
   */
  private cleanup() {

    if (this.scrollListener) {
      this.scrollListener();
      this.scrollListener = null;
    }

    if (this.tabChangeObserver) {
      this.tabChangeObserver.disconnect();
      this.tabChangeObserver = null;
    }

    if (this.pageActivationObserver) {
      this.pageActivationObserver.disconnect();
      this.pageActivationObserver = null;
    }

    this.scrollAreaElement = null;
    this.registeredComponents.clear();
  }

  /**
   * Force a refresh of scroll detection (useful when page structure changes)
   */
  refresh() {
    if (this.registeredComponents.size > 0) {
      this.setupScrollDetection();
    }
  }

  /**
   * Temporarily suspend scroll-hide detection until the next animation frame.
   * Use this before programmatic scrolls (e.g., scrollIntoView) to prevent
   * the header from hiding during initial page load scrolls.
   */
  suspendUntilNextFrame() {
    this.isSuspended = true;
    requestAnimationFrame(() => {
      this.isSuspended = false;
      // reset scroll tracking state after suspension ends
      if (this.scrollAreaElement) {
        this.globalLastScrollTop = this.scrollAreaElement.scrollTop || 0;
      }
      this.scrollAccumulator = 0;
      this.lastScrollDirection = null;
    });
  }
}